import apiClient from './api';

// Discount interface matching backend structure
export interface Discount {
  code: string;
  value: number;
  type: 'percent' | 'number';
  categories?: string[];
  vendors?: string[];
  maxUsage?: number;
  usageCount?: number;
  expires?: string;
  status:  'all' | 'Active' | 'Archived';
  createdAt: string;
  updatedAt: string;
}

// Pagination interface
export interface DiscountsPagination {
  hasMore: boolean;
  currentPage: number;
  totalItems: number;
  totalPages: number;
}

// API Response interface for discounts list
export interface DiscountsApiResponse {
  message?: string;
  success?: boolean;
  data?: {
    success: boolean;
    message: string;
    promoCodes: Discount[];
    pagination: DiscountsPagination;
  };
  promoCodes?: Discount[];
  pagination?: DiscountsPagination;
  promoCode?: Discount; // For single discount responses
}

// Frontend interface for discounts (transformed)
export interface DiscountDisplay {
  id: string;
  code: string;
  discount: string; // Formatted as "5%" or "$10"
  discountValue: number;
  discountType: 'percent' | 'number';
  categories: string; // Formatted as "All categories" or comma-separated list
  vendors?: string[]; // Array of vendor IDs
  expires: string; // Formatted date
  uses: number;
  maxUsage?: number;
  status:  'all' | 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
}

// Transform backend discount to frontend format
const transformDiscount = (discount: Discount): DiscountDisplay => {
  // Format discount
  const discountFormatted = discount.type === 'percent' 
    ? `${discount.value}%` 
    : `$${discount.value}`;

  // Format categories
  let categories = 'All categories';
  if (discount.categories && discount.categories.length > 0) {
    categories = discount.categories.join(', ');
  }

  // Format expiration date
  let expires = 'Never';
  if (discount.expires) {
    const date = new Date(discount.expires);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    expires = `${day} ${month}, ${year}`;
  }

  return {
    id: discount.code,
    code: discount.code,
    discount: discountFormatted,
    discountValue: discount.value,
    discountType: discount.type,
    categories,
    vendors: discount.vendors,
    expires,
    uses: discount.usageCount || 0,
    maxUsage: discount.maxUsage,
    status: discount.status.toLowerCase() as 'all' | 'active' | 'archived',
    createdAt: discount.createdAt,
    updatedAt: discount.updatedAt,
  };
};

export const discountsService = {
  // Get all discounts with optional status, category, and vendor filters and pagination
  getDiscounts: async (
    status?:  'all'  | 'Active' | 'Archived',
    page: number = 1,
    limit: number = 10,
    categories?: string[],
    vendors?: string[]
  ): Promise<{ discounts: DiscountDisplay[]; pagination: DiscountsPagination }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      pageBased: 'true',
    });
    
    // Only add status if provided (not for 'all' tab)
    if (status) {
      params.append('status', status);
    }
    
    // Only add categories if provided and not empty
    if (categories && categories.length > 0) {
      params.append('categories', categories.join(','));
    }
    
    // Only add vendors if provided and not empty
    if (vendors && vendors.length > 0) {
      params.append('vendors', vendors.join(','));
    }

    console.log('API Call - Endpoint:', `/promo-codes?${params.toString()}`);
    const response = await apiClient.get<DiscountsApiResponse>(`/promo-codes?${params.toString()}`);
    const data = response.data;

    // Handle different response structures
    let discounts: Discount[] = [];
    let pagination: DiscountsPagination = {
      hasMore: false,
      currentPage: page,
      totalItems: 0,
      totalPages: 1,
    };

    // Handle direct structure: { success: true, promoCodes: [...], pagination: {...} }
    if (data.success && data.promoCodes && Array.isArray(data.promoCodes)) {
      discounts = data.promoCodes;
      pagination = data.pagination || pagination;
    }
    // Handle nested structure: { message, data: { success, promoCodes, pagination } }
    else if (data.data && data.data.promoCodes && Array.isArray(data.data.promoCodes)) {
      discounts = data.data.promoCodes;
      pagination = data.data.pagination || pagination;
    }
    // Fallback for direct array
    else if (Array.isArray(data)) {
      discounts = data;
    }

    return {
      discounts: discounts.map(transformDiscount),
      pagination,
    };
  },

  // Get discount by code
  getDiscount: async (code: string): Promise<DiscountDisplay> => {
    const response = await apiClient.get<{ success: boolean; promoCode: Discount }>(`/promo-codes/${code}`);
    const data = response.data;
    
    if (data.promoCode) {
      return transformDiscount(data.promoCode);
    }
    
    throw new Error('Discount not found');
  },

  // Create new discount
  createDiscount: async (discount: Omit<Discount, 'createdAt' | 'updatedAt' | 'usageCount'>): Promise<DiscountDisplay> => {
    try {
      const response = await apiClient.post<DiscountsApiResponse>('/promo-codes', discount);
      const data = response.data;
      
      // Handle different response structures
      let promoCode: Discount | undefined;
      
      // Check for direct promoCode in response
      if (data.promoCode) {
        promoCode = data.promoCode;
      }
      // Check for nested structure: { data: { promoCode: ... } }
      else if (data.data && (data.data as any).promoCode) {
        promoCode = (data.data as any).promoCode;
      }
      // Check for array response (unlikely but possible)
      else if (Array.isArray(data) && data.length > 0) {
        promoCode = data[0];
      }
      
      if (promoCode) {
        return transformDiscount(promoCode);
      }
      
      // Log the actual response for debugging
      console.error('Unexpected API response structure:', data);
      throw new Error('Failed to create discount: Unexpected response structure');
    } catch (error: any) {
      // If it's an axios error, log the response data
      if (error.response) {
        console.error('API Error Response:', error.response.data);
        console.error('API Error Status:', error.response.status);
      }
      throw error;
    }
  },

  // Update discount
  updateDiscount: async (code: string, updates: Partial<Discount>): Promise<DiscountDisplay> => {
    try {
      // Send updates as-is - backend should handle discountValue field
      // (workaround for DynamoDB reserved keyword "value")
      console.log('Updating discount with payload:', updates);
      const response = await apiClient.patch<any>(`/promo-codes/${code}`, updates);
      const data = response.data;
      
      let promoCode: Discount | undefined;
      
      // Handle different response structures (similar to createDiscount)
      // Check for direct structure: { promoCode: ... }
      if (data.promoCode) {
        promoCode = data.promoCode;
      }
      // Check for nested structure: { data: { promoCode: ... } }
      else if (data.data && (data.data as any).promoCode) {
        promoCode = (data.data as any).promoCode;
      }
      // Check for array response (unlikely but possible)
      else if (Array.isArray(data) && data.length > 0) {
        promoCode = data[0];
      }
      // Check if the response itself is the promo code
      else if (data.code) {
        promoCode = data;
      }
      
      if (promoCode) {
        return transformDiscount(promoCode);
      }
      
      // If update was successful but no promo code returned, fetch it
      if (data.success || response.status === 200) {
        console.log('Update successful, fetching updated discount...');
        // Fetch the updated discount using the getDiscount method
        const getDiscountResponse = await apiClient.get<{ success: boolean; promoCode: Discount }>(`/promo-codes/${code}`);
        const getDiscountData = getDiscountResponse.data;
        if (getDiscountData.promoCode) {
          return transformDiscount(getDiscountData.promoCode);
        }
      }
      
      // Log the actual response for debugging
      console.error('Unexpected API response structure:', data);
      console.error('Response status:', response.status);
      throw new Error('Failed to update discount: Unexpected response structure');
    } catch (error: any) {
      console.error('Error updating discount:', error);
      if (error.response?.data) {
        console.error('Error response data:', error.response.data);
      }
      // If it's an axios error, log the response data
      if (error.response?.data) {
        console.error('Full error response:', JSON.stringify(error.response.data, null, 2));
      }
      throw error;
    }
  },

  // Delete/Archive discount
  archiveDiscount: async (code: string): Promise<void> => {
    await apiClient.patch(`/promo-codes/${code}/archive`);
  },
};

